/**
 * DraftSelectorModal Component
 * Bottom sheet for selecting a draft or starting a new check-in
 */

import { Button } from '@/components/ui/button';
import { confirm } from '@/components/ui/confirmation-dialog';
import { Sheet, SHEET_SNAP_POINTS } from '@/components/ui/sheet';
import { Text } from '@/components/ui/text';
import Ionicons from '@expo/vector-icons/Ionicons';
import React from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { CheckInDraft } from '../hooks/use-drafts';

/**
 * Below this many drafts the list is short enough that a snug, content-sized
 * sheet beats a fixed-height one with empty space; above it, the list is worth
 * expanding to read.
 */
const EXPANDABLE_DRAFT_COUNT = 2;

export interface DraftSelectorModalProps {
  visible: boolean;
  onClose: () => void;
  drafts: CheckInDraft[];
  onSelectDraft: (draftId: string) => void;
  onStartNew: () => void;
  onDeleteDraft: (draftId: string) => void;
}

export function DraftSelectorModal({
  visible,
  onClose,
  drafts,
  onSelectDraft,
  onStartNew,
  onDeleteDraft,
}: DraftSelectorModalProps) {
  const handleDeleteDraft = async (draftId: string) => {
    const confirmed = await confirm({
      title: 'Delete draft?',
      description: 'Are you sure you want to delete this draft? This action cannot be undone.',
      variant: 'destructive',
      confirmLabel: 'Delete',
    });
    if (confirmed) {
      onDeleteDraft(draftId);
    }
  };

  const expandable = drafts.length > EXPANDABLE_DRAFT_COUNT;

  return (
    <Sheet
      visible={visible}
      onClose={onClose}
      snapPoints={expandable ? SHEET_SNAP_POINTS : undefined}
      testID="draft-selector-modal"
    >
      <View className="flex-col" style={expandable ? { flex: 1 } : undefined}>
        {/* Header */}
        <View className="p-5 pb-4 border-b border-border shrink-0">
          <View className="flex-row justify-between items-center">
            <Text className="text-xl font-semibold text-foreground">
              Check-in
            </Text>
            <Pressable
              onPress={onClose}
              hitSlop={{ top: 10, right: 10, bottom: 10, left: 10 }}
            >
              <Ionicons name="close" size={24} className="text-muted-foreground" />
            </Pressable>
          </View>
          {drafts.length > 0 && (
            <Text className="text-sm text-muted-foreground mt-1">
              You have {drafts.length} saved {drafts.length === 1 ? 'draft' : 'drafts'}
            </Text>
          )}
        </View>

        {/* Content */}
        {/* Expandable: the sheet has a fixed height, so the list fills what's left.
            Otherwise the cap bounds the ScrollView so the sheet fits its content
            below that height and scrolls rather than overflowing above it. */}
        <ScrollView
          style={expandable ? { flex: 1 } : { flexGrow: 1, flexShrink: 1, maxHeight: 300 }}
          contentContainerStyle={{ padding: 16, paddingBottom: 8 }}
          showsVerticalScrollIndicator={true}
        >
          {/* Drafts List */}
          {drafts.length > 0 && (
            <View className="mb-4">
              <Text className="text-sm font-semibold text-muted-foreground mb-3 uppercase tracking-wide">
                Saved Drafts
              </Text>
              {drafts.map((draft) => (
                <Pressable
                  key={draft.id}
                  onPress={() => onSelectDraft(draft.id)}
                  className="mb-2.5 p-3.5 rounded-xl bg-background border border-border flex-row items-center justify-between"
                >
                  <View className="flex-1 mr-3">
                    <Text
                      className="text-base font-semibold text-foreground mb-0.5"
                      numberOfLines={1}
                    >
                      {draft.business?.name || 'Unknown Business'}
                    </Text>
                    {draft.entries && draft.entries.length > 1 ? (
                      <Text
                        className="text-sm text-muted-foreground mb-0.5"
                        numberOfLines={1}
                      >
                        {draft.entries.length} items
                      </Text>
                    ) : draft.menuItem ? (
                      <Text
                        className="text-sm text-muted-foreground mb-0.5"
                        numberOfLines={1}
                      >
                        {draft.menuItem.name}
                      </Text>
                    ) : null}
                    <Text className="text-xs text-muted-foreground">
                      Updated {new Date(draft.updatedAt).toLocaleDateString()}
                    </Text>
                  </View>

                  {/* Delete button */}
                  <Pressable
                    onPress={(e) => {
                      e.stopPropagation();
                      handleDeleteDraft(draft.id);
                    }}
                    style={{ padding: 8 }}
                    hitSlop={{ top: 10, right: 10, bottom: 10, left: 10 }}
                  >
                    <Ionicons name="trash-outline" size={18} className="text-destructive" />
                  </Pressable>

                  <Ionicons name="chevron-forward" size={18} className="text-muted-foreground" />
                </Pressable>
              ))}
            </View>
          )}
        </ScrollView>

        {/* Footer */}
        <View className="p-4 pt-3 border-t border-border shrink-0">
          <Button
            onPress={onStartNew}
            variant="primary"
            fullWidth
          >
            <Text className="text-base text-primary-foreground font-semibold">Start New Check-in</Text>
          </Button>
        </View>
      </View>
    </Sheet>
  );
}

export default DraftSelectorModal;
